import React, { useEffect, useState } from 'react';
import Layout from '../Layout/Layout';
import { isAuthenticated } from '../auth';
import { getMyAppearance, saveMyAppearance } from '../auth/appearanceApi';
import { elakayAutoText } from '../pageInside/dataElakay';
import { DESIGN_CHOICES, PLATFORM_CHOICE_ID, designToPersonal, getDesign, personalToDesign, resolvePlatformDesign } from '../Layout/headerFooterCollections';
import { designDescription, designName } from '../Layout/headerFooterCollectionsText';
import './AccountSettings.css';

const t = (key, fallback, values) => elakayAutoText(key, fallback, values);

// Personal Header/Footer design. Personalization stays OFF until World Family
// enables it in Animation Settings; while "Use Original Design" is ON the
// Original is shown to everyone and the saved choice simply waits.
const AccountSettings = ({ infoSource }) => {
  const token = isAuthenticated()?.token;
  const config = infoSource?.allSettings?.headerFooterCollections || {};
  const enabled = config.allowUserCustomization === true;
  const originalForced = config.useOriginalDesign !== false;
  const platformName = designName(getDesign(resolvePlatformDesign(config)));
  const [choice, setChoice] = useState(PLATFORM_CHOICE_ID);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    getMyAppearance(token)
      .then(data => {
        const id = data.appearance?.headerFooterCollectionId;
        setChoice(personalToDesign(id) ? id : PLATFORM_CHOICE_ID);
      })
      .catch(error => setMessage({ type: 'error', text: error.message }));
  }, [token]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await saveMyAppearance(token, choice);
      await infoSource?.readUser?.(token, ['getUserInfo']);
      setMessage({ type: 'success', text: t('h_hfc_acc_saved', 'Your design preference is saved.') });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const options = [
    { id: PLATFORM_CHOICE_ID, name: t('h_hfc_acc_follow', 'Follow the platform'), description: t('h_hfc_acc_follow_desc', "Always use the design chosen by World Developer."), palette: [] },
    ...DESIGN_CHOICES.map(design => ({ id: designToPersonal(design.id), name: designName(design), description: designDescription(design), palette: design.palette })),
  ];

  return <Layout infoSource={infoSource} titleElakay="accountSettings">
    <main className="account-settings-page">
      <header>
        <span>{t('h_hfc_acc_kicker', 'ELAKAY ACCOUNT')}</span>
        <h1>{t('h_account_settings', 'Account Settings')}</h1>
        <p>{t('h_hfc_acc_intro', 'Manage your personal experience without changing platform-wide settings.')}</p>
      </header>
      <section aria-labelledby="appearance-title">
        <div>
          <h2 id="appearance-title">{t('h_hfc_acc_appearance', 'Header & Footer appearance')}</h2>
          <p>{enabled
            ? t('h_hfc_acc_enabled', 'Choose the design of the Header and Footer for your account.')
            : t('h_hfc_acc_disabled', "Personalization is turned off by World Developer. You see the platform design: {name}.", { name: platformName })}</p>
          {enabled && originalForced && (
            <p className="account-settings-note">{t('h_hfc_acc_original_forced', "World Developer currently shows the Original design to everyone. Your choice is kept and applies as soon as that is turned off.")}</p>
          )}
        </div>
        <div className="account-collection-grid" role="radiogroup" aria-labelledby="appearance-title">
          {options.map(item => (
            <label key={item.id} className={choice === item.id ? 'selected' : ''}>
              <input type="radio" name="collection" value={item.id} checked={choice === item.id} disabled={!enabled || saving} onChange={() => setChoice(item.id)} />
              {item.palette.length > 0 && <span className="account-swatches">{item.palette.map(color => <i key={color} style={{ background: color }} />)}</span>}
              <strong>{item.name}</strong>
              <small>{item.description}</small>
            </label>
          ))}
        </div>
        <button type="button" disabled={!enabled || saving} onClick={save}>{t('h_hfc_acc_save', 'Save my design')}</button>
        {message && <p className={`account-settings-message is-${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>{message.text}</p>}
      </section>
    </main>
  </Layout>;
};

export default AccountSettings;
